import { Button, Field, Icon, Textbox, Validity } from "@iyk/ui"
import { FormState, useForm } from "@iyk/vf-forms"
import { Analytics, useAnalytics } from "@iyk/vf-observability/react"
import { Terms } from "@iyk/vf-ui"
import { appConfig } from "../../app-config.ts"
import { useLang } from "../../react-router/use-lang.ts"

export const LoginForm = ({ returnTo, onSubmit, action }: LoginForm.Props) => {
  const lang = useLang()
  return (
    <>
      <GoogleForm returnTo={returnTo} action={action} onSubmit={onSubmit} />
      <div className="flex items-center gap-2">
        <hr className="flex-1 text-gray-4" />
        <span className="text-sm text-gray-9">or</span>
        <hr className="flex-1 text-gray-4" />
      </div>
      <EmailForm returnTo={returnTo} action={action} onSubmit={onSubmit} />
      <Terms
        privacyPolicyUrl={appConfig.assets.documents.privacyPolicy}
        termsOfServiceUrl={appConfig.assets.documents.termsOfService}
        artistPrivacyPolicy={
          appConfig.assets.documents.artistPrivacyPolicy
            ? {
                url: appConfig.assets.documents.artistPrivacyPolicy,
                name: appConfig.artist.name
              }
            : undefined
        }
        lang={lang}
      />
    </>
  )
}

const GoogleForm = ({
  returnTo,
  action,
  onSubmit
}: {
  returnTo: string
  action?: string
  onSubmit?: (payload: { method: "google" }) => void
}) => {
  const { Form } = useForm()
  const analytics = useAnalytics()

  return (
    <Form
      method="post"
      action={action}
      className="flex flex-col gap-3"
      onSubmit={() => {
        analytics.track(Analytics.Event.LoginSubmitted({ method: "google" }))
        onSubmit?.({ method: "google" })
      }}
    >
      <input type="hidden" name="returnTo" value={returnTo} />
      <input type="hidden" name="_tag" value="OAuth" />

      <Button type="submit" name="provider" value="Google" kind="outline" size="md" className="w-full">
        <Icon.Google />
        Continue with Google
      </Button>
    </Form>
  )
}

const EmailForm = ({
  returnTo,
  onSubmit,
  action
}: {
  returnTo: string
  onSubmit?: (payload: { method: "email"; email: string }) => void
  action?: string
}) => {
  const { Form, errors, state } = useForm()
  const analytics = useAnalytics()

  const isDisabled = FormState.$match(state, {
    Submitting: () => true,
    Failed: () => false,
    Idle: () => false,
    Succeeded: () => false
  })

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(e.currentTarget)
    const email = formData.get("email")?.toString()
    if (email) {
      onSubmit?.({ method: "email", email })
    }
  }

  return (
    <Form
      method="post"
      action={action}
      className="flex flex-col gap-3 mt-3"
      onSubmit={(e) => {
        analytics.track(Analytics.Event.LoginSubmitted({ method: "email" }))
        if (onSubmit) handleOnSubmit(e)
      }}
      navigate={onSubmit ? false : true}
    >
      <input type="hidden" name="returnTo" value={returnTo} />
      <input type="hidden" name="_tag" value="Otp" />

      <Field name="email" required validationMessage={errors?.email}>
        <Textbox type="email" autoComplete="email" placeholder="Email" />
        <Validity />
      </Field>

      <Button type="submit" kind="solid" size="lg" className="w-full items-center" disabled={isDisabled}>
        {isDisabled && <Icon.Loading className="size-2.5" />}
        Continue with email
      </Button>
    </Form>
  )
}

export namespace LoginForm {
  export interface Props {
    returnTo: string
    onSubmit?: (payload: { method: "google" } | { method: "email"; email: string }) => void
    action?: string
  }
}
